/* eslint-disable no-magic-numbers */
/* eslint-disable @typescript-eslint/no-explicit-any */

import {
  AmountCell,
  CountryCell,
  DateCell,
  DateFormats,
  Inline,
  Stack,
  Tag,
  Typography,
} from '@omnipresentgroup/design-system';
import { customArrayIncludesSome } from '@omnipresentgroup/shared';
import {
  type CombinedEmployee,
  CombinedEmployeeType,
} from '@omnipresentgroup/types';
import {
  type ColumnDef,
  createColumnHelper,
  sortingFns,
} from '@tanstack/react-table';
import { useMemo } from 'react';

import { EmployeeStatus } from '../../employee-status';

export enum ColumnHeaders {
  id = 'Employee',
  jobTitle = 'Role',
  country = 'Country',
  annualBaseSalary = 'Salary',
  type = 'Type',
  startDate = 'Start date',
  onboardingStatus = 'Status',
}

export const useEmployeesTableConfig = ({
  columnsOverride,
}: {
  columnsOverride?: ColumnDef<CombinedEmployee, unknown>[];
}) => {
  const columnHelper = useMemo(
    () => createColumnHelper<CombinedEmployee>(),
    [],
  );

  const columns: ColumnDef<CombinedEmployee, any>[] = useMemo(
    () => [
      columnHelper.accessor('id', {
        cell: (info) => {
          return (
            <Inline justify="space-between">
              <Stack gap="0">
                <Typography as="span" size="14">
                  {info.row.original.name || '-'}
                </Typography>

                <Typography as="span" size="12" color="subtle">
                  {info.row.original.name
                    ? info.row.original.jobTitle || '-'
                    : info.row.original.id}
                </Typography>
              </Stack>
              {info.row.original.type !== CombinedEmployeeType.EMPLOYEE && (
                <Tag id={`${info.row.original.id}-source`} outlined>
                  {`${info.row.original.type
                    .charAt(0)
                    .toUpperCase()}${info.row.original.type.slice(1).toLowerCase()}`}
                </Tag>
              )}
            </Inline>
          );
        },
        header: () => ColumnHeaders.id,
        size: 300,
      }),
      columnHelper.accessor('jobTitle', {
        cell: (info) => info.getValue(),
        header: () => ColumnHeaders.jobTitle,
      }),
      columnHelper.accessor('type', {
        cell: (info) => info.getValue(),
        header: () => ColumnHeaders.type,
      }),
      columnHelper.accessor('country', {
        cell: (info) => <CountryCell countryName={info.getValue()} />,
        header: () => ColumnHeaders.country,
        filterFn: customArrayIncludesSome,
      }),
      columnHelper.accessor('annualBaseSalary', {
        cell: (info) => {
          return (
            <Stack gap="0">
              <AmountCell
                amount={info.row.original.annualBaseSalary.amount}
                currency={info.row.original.annualBaseSalary.currency}
              />
              {info.row.original.nextCutoffDate && (
                <Inline gap="4">
                  <Typography as="span" size="12" color="subtle">
                    Next cutoff:
                  </Typography>
                  <DateCell
                    value={info.row.original.nextCutoffDate}
                    dateFormat={DateFormats.DateTime}
                    size="12"
                    color="subtle"
                    showTimezone
                  />
                </Inline>
              )}
            </Stack>
          );
        },
        header: () => ColumnHeaders.annualBaseSalary,
      }),
      columnHelper.accessor('startDate', {
        cell: (info) => <DateCell value={info.getValue()} />,
        sortingFn: sortingFns.datetime,
        header: () => ColumnHeaders.startDate,
        size: 75,
      }),
      columnHelper.accessor('onboardingStatus', {
        cell: (info) => <EmployeeStatus status={info.getValue()} />,
        header: () => ColumnHeaders.onboardingStatus,
        filterFn: customArrayIncludesSome,
        size: 90,
      }),
      ...(columnsOverride ?? []),
    ],
    [columnHelper, columnsOverride],
  );

  return {
    columns,
    columnHelper,
  };
};
